import React, { useState, useEffect } from 'react';
import useFilters from 'utils/hooks/useFilters';
import i18nIsoCountries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import CustomTable from 'components/CustomTable/CustomTable';
import { theme, Button, notification, Modal } from 'antd';
import { article8Op } from 'store/ducks/DataManagement/Article8AndICTSystem';
import { IEntityRole } from 'store/ducks/DataManagement/Article8AndICTSystem/types';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { getEntityRoleColumns } from 'components/Columns/EntityRole';
import SaveButton from 'components/SaveButton/SaveButton';
import { useTranslation } from 'react-i18next';
import * as Styled from '../../DataEntry/DataEntry.styled';
import { deleteRecord } from 'utils/hooks/deleteRecord';

const EntityRole: React.FC = () => {
  i18nIsoCountries.registerLocale(enLocale);
  const { query } = useFilters();
  const { useToken } = theme;
  const { t } = useTranslation();
  const [dataSource, setDataSource] = useState<IEntityRole[]>([]);
  const [count, setCount] = useState(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const { token } = useToken();
  const isLoggedIn = localStorage.getItem('authToken');
  const [roleTypes, setRoleTypes] = useState<
    { label: string; value: string }[]
  >([]);
  const { confirm } = Modal;
  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const fetchRoleTypes = async () => {
    try {
      const response = await article8Op.getRoleType();
      if (response?.data.role_types) {
        const formattedOptions = response.data.role_types.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setRoleTypes(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchEntityRoleByEntity = async () => {
    try {
      const response = await article8Op.getEntityRole();
      const data = response?.data.entity_roles;
      const mappedData = Array.isArray(data)
        ? data.map((item, index) => ({
            key: index,
            active: item.active,
            deleted: item.deleted,
            role_type_id: item.role_type_id,
            role_type_name: item.role_type_name,
            role_name: item.role_name,
            description: item.description,
            id: item.id,
            entity_id: item.entity_id,
            entity_name: item.entity_name,
            isNew: false,
            isEdited: false,
          }))
        : data
          ? [
              {
                key: 0,
                active: data.active,
                deleted: data.deleted,
                role_type_id: data.role_type_id,
                role_type_name: data.role_type_name,
                role_name: data.role_name,
                description: data.description,
                id: data.id,
                entity_id: data.entity_id,
                entity_name: data.entity_name,
                isNew: false,
                isEdited: false,
              },
            ]
          : [];
      setDataSource(mappedData);
      setCount(mappedData.length);
    } catch (error) {
      setDataSource([]);
      console.log(error, 'error');
    }
  };

  useEffect(() => {
    if (query.entityId) {
      fetchEntityRoleByEntity();
      fetchRoleTypes();
    } else {
      setDataSource([]);
    }
  }, [query.entityId, query.entity]);

  const handleAddRow = (record?: any, index?: number) => {
    const newData: IEntityRole = {
      key: generateUniqueId(),
      active: true,
      deleted: false,
      role_type_id: '',
      role_type_name: '',
      entity_name: '',
      description: '',
      entity_id: '',
      id: generateUniqueId(),
      role_name: '',
      isNew: true,
    };
    const insertIndex = dataSource.findIndex(
      (item) => item.key === record?.key,
    );

    const updatedDataSource = [
      ...dataSource.slice(0, insertIndex + 1),
      newData,
      ...dataSource.slice(insertIndex + 1),
    ];

    setDataSource(updatedDataSource);
    setCount(count + 1);
  };

  const handleSave = (row: IEntityRole) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    newData.splice(index, 1, {
      ...newData[index],
      ...row,
      isEdited: !newData[index].isNew,
    });
    setDataSource(newData);
  };

  const handleRemoveRow = (key: number) => {
    Modal.confirm({
      title: 'Are you sure you want to remove this row?',
      content:
        'Once deleted, this record cannot be restored. Would you like to continue?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        const updatedDataSource = dataSource.filter((item) => item.key !== key);
        setDataSource(updatedDataSource);
        notification.success({
          message: 'Info',
          description: 'The row has been successfully removed.',
          duration: 20,
        });
      },
      onCancel: () => {
        notification.info({
          message: 'Info',
          description: 'The row removal has been canceled.',
          duration: 20,
        });
      },
    });
  };

  const handleInactivate = (record: IEntityRole) => {
    if (!record.id) {
      notification.error({
        message: 'Info',
        description: 'Id is missing.',
      });
      return;
    }

    deleteRecord({
      id: record.id,
      tableName: 'entity_role',
      onSuccess: () => {
        fetchEntityRoleByEntity();
      },
    });
  };

  const columns = getEntityRoleColumns(
    handleSave,
    handleAddRow,
    handleRemoveRow,
    handleInactivate,
    roleTypes,
    t,
  );

  const handleSubmit = async () => {
    const changedData = dataSource.filter(
      (item) => item.isNew || item.isEdited,
    );

    if (changedData.length === 0) {
      notification.warning({
        message: 'Info',
        description: 'There are no new or edited data to submit.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }
    const formattedData = {
      entity_name: query.entity,
      data_list: changedData.map((item) => ({
        role_type_id: item.role_type_id,
        role_type_name: item.role_type_name,
        entity_name: query.entity,
        description: item.description,
        entity_id: query.entityId,
        id: item.id,
        role_name: item.role_name,
      })),
      token: isLoggedIn,
    };

    try {
      const response = await article8Op.insertEntityRole(formattedData);
      if (response?.status === 200) {
        setDataSource((prev) =>
          prev.map((item) =>
            item.isNew || item.isEdited
              ? { ...item, isNew: false, isEdited: false }
              : item,
          ),
        );
        notification.success({
          message: 'Info',
          description: 'The data was successfully submitted.',
          placement: 'topRight',
          duration: 20,
        });
      }
    } catch (error) {
      notification.error({
        message: 'Info',
        description: 'There was an error sending the data. Please try again.',
        placement: 'topRight',
        duration: 20,
      });
    }
  };

  return (
    dataSource && (
      <>
        <Styled.HighlightedTable>
          <CustomTable
            columns={columns}
            dataSource={dataSource}
            handleSave={handleSave}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              onChange: handlePageChange,
              showSizeChanger: true,
            }}
            locale={
              query.entity
                ? {
                    emptyText: (
                      <Button type="primary" onClick={() => handleAddRow()}>
                        {t('GENERAL.ADD_FIRST_ROW')}
                      </Button>
                    ),
                  }
                : undefined
            }
          />
        </Styled.HighlightedTable>

        <Styled.ButtonRow>
          {dataSource.length > 0 && (
            <Styled.AreaButton
              token={token}
              type="primary"
              onClick={() => handleAddRow()}
            >
              Add new row
            </Styled.AreaButton>
          )}
          {dataSource.length > 0 && (
            <SaveButton
              token={token}
              hasChanges={dataSource.some((i) => i.isNew || i.isEdited)}
              onClick={handleSubmit}
            />
          )}
        </Styled.ButtonRow>
      </>
    )
  );
};

export default EntityRole;
